import React, { ReactElement } from 'react'
import { Box, BoxProps, TextField, TextFieldProps, Typography } from '@material-ui/core'
import { Control, Controller, FieldError, FieldName, FieldValues, Validate } from 'react-hook-form'

interface IProps<T extends FieldValues> {
  boxProps?: BoxProps
  control: Control<T>
  'data-e2e'?: string
  displayCounter?: boolean
  error?: FieldError
  errorText?: React.ReactNode
  helperText?: React.ReactNode
  id: string
  inputProps?: TextFieldProps['inputProps']
  label?: string
  maxLength?: number
  multiline?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  placeholder?: string
  rows?: number
  type?: 'string' | 'number' | 'password'
  variant?: 'outlined' | 'standard'
  size?: 'small' | 'medium'
  required?: boolean
  pattern?: RegExp
  validate?: Validate | Record<string, Validate>
  endAdornment?: ReactElement
  disabled?: boolean
}

const styles = {
  helperContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: '-14px',
    marginRight: '-14px',
  },
  helperText: {
    color: '#000',
  },
  helperTextCounter: {
    color: '#000',
    marginLeft: 'auto',
  },
}

const FormTextField = <TFieldValues extends FieldValues>({
  boxProps,
  control,
  displayCounter,
  id,
  inputProps,
  label,
  error,
  helperText,
  maxLength,
  multiline,
  placeholder,
  rows,
  type = 'string',
  variant = 'outlined',
  size,
  required = false,
  errorText,
  pattern,
  validate,
  endAdornment,
  disabled,
  ...props
}: IProps<TFieldValues>) => {
  const helperTextComponent = (value = '') => {
    return (
      <Box sx={styles.helperContainer}>
        {helperText && (
          <Typography variant="caption">
            {helperText}
          </Typography>
        )}
        {displayCounter && (
          <Typography component="span">{`${value.length}/${
            maxLength || 'unlimited'
          }`}</Typography>
        )}
      </Box>
    )
  }

  return (
    <Box {...boxProps}>
      <Controller
        control={control}
        rules={{ required, pattern, validate }}
        name={id as FieldName<TFieldValues>}
        render={({ onChange, value }) => (
          <TextField
            data-e2e={props['data-e2e']}
            error={!!error}
            fullWidth
            helperText={error && errorText ? errorText : (error ? error.message : helperTextComponent(value))}
            id={id}
            inputProps={{
              maxLength: maxLength,
              disabled: disabled,
              ...inputProps,
            }}
            InputProps={{
              endAdornment
            }}
            label={label}
            multiline={multiline}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (props.onChange) {
                props.onChange(event)
              }
              onChange(event)
            }}
            placeholder={placeholder}
            rows={rows}
            size={size}
            type={type}
            value={value}
            variant={variant}
          />
        )}
      />
    </Box>
  )
}

export default FormTextField
